import React, { useEffect, useState } from "react";
import { TextField, TextFieldProps } from "@mui/material";

type TimeField24Props = Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value: string; // "HH:MM" (24h)
  onChange: (value: string) => void;
};

/** Normalises user input like "9", "915", "9:5" or "21:30" to "HH:MM"; null if not a valid 24h time. */
const normalizeTime = (raw: string): string | null => {
  const digits = raw.trim();
  let h: number;
  let m: number;
  if (/^\d{1,2}:\d{1,2}$/.test(digits)) {
    [h, m] = digits.split(":").map(Number);
  } else if (/^\d{1,2}$/.test(digits)) {
    h = Number(digits);
    m = 0;
  } else if (/^\d{3,4}$/.test(digits)) {
    h = Number(digits.slice(0, -2));
    m = Number(digits.slice(-2));
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * 24-hour time input (e.g. 09:15, 21:30). The native time input follows the browser's locale
 * and shows AM/PM, so this uses a text field that always displays and stores 24h "HH:MM".
 */
export default function TimeField24({ value, onChange, ...rest }: TimeField24Props) {
  const [text, setText] = useState(value || "");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(value || "");
    setInvalid(false);
  }, [value]);

  const commit = () => {
    const normalized = normalizeTime(text);
    if (normalized) {
      setInvalid(false);
      setText(normalized);
      if (normalized !== value) onChange(normalized);
    } else {
      setInvalid(text.trim() !== "");
    }
  };

  return (
    <TextField
      {...rest}
      value={text}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d:]/g, "").slice(0, 5);
        setText(v);
        const normalized = /^\d{2}:\d{2}$/.test(v) ? normalizeTime(v) : null;
        if (normalized) {
          setInvalid(false);
          onChange(normalized);
        }
      }}
      onBlur={commit}
      error={invalid || rest.error}
      placeholder="HH:MM"
      inputProps={{ inputMode: "numeric", maxLength: 5, ...(rest.inputProps || {}) }}
      helperText={invalid ? "Use 24-hour format, e.g. 09:15 or 18:30" : rest.helperText}
    />
  );
}
